import React from "react";
import { useMsal } from "@azure/msal-react";
import {
  Badge,
  Button,
  Card,
  MessageBar,
  MessageBarBody,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowRight24Regular,
  LockClosed24Regular,
  ShieldCheckmark24Regular,
} from "@fluentui/react-icons";
import { API_BASE_URL, LOCAL_DV_MODE } from "../config";
import { checkRegistrationStatus, startPopupLogin } from "../services/authService";
import { taskPaneBackground } from "../theme";

const logoFilled = require("../../../assets/sstone-logo-v2-128.png");

const useStyles = makeStyles({
  root: {
    position: "relative",
    display: "flex",
    minHeight: "85vh",
    alignItems: "center",
    justifyContent: "center",
    overflowY: "auto",
    ...shorthands.padding("32px", "20px"),
    color: tokens.colorNeutralForeground1,
    ...taskPaneBackground,
  },
  card: {
    position: "relative",
    flexShrink: 0,
    width: "100%",
    maxWidth: "370px",
    overflow: "hidden",
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("14px"),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
  },
  header: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    ...shorthands.padding("28px", "24px", "16px"),
    textAlign: "center",
  },
  logoFrame: {
    display: "flex",
    width: "56px",
    height: "56px",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "12px",
    ...shorthands.border("1px", "solid", "#dbeafe"),
    ...shorthands.borderRadius("16px"),
    backgroundColor: "#eff6ff",
  },
  logo: {
    width: "40px",
    height: "40px",
    objectFit: "contain",
  },
  badge: {
    marginBottom: "8px",
    color: "#1e40af",
    backgroundColor: "#eff6ff",
    ...shorthands.borderColor("#dbeafe"),
  },
  title: {
    margin: 0,
    fontSize: "24px",
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: "1.25",
    letterSpacing: "-0.035em",
  },
  description: {
    maxWidth: "285px",
    marginTop: "8px",
    marginBottom: 0,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: "20px",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("16px"),
    ...shorthands.padding("0", "20px", "20px"),
  },
  securityPanel: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("12px"),
    ...shorthands.padding("12px", "14px"),
    ...shorthands.border("1px", "solid", "#dbeafe"),
    ...shorthands.borderRadius("12px"),
    backgroundColor: "#f5f9ff",
  },
  securityIcon: {
    display: "flex",
    width: "32px",
    height: "32px",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    color: "#1d4ed8",
    ...shorthands.border("1px", "solid", "#dbeafe"),
    ...shorthands.borderRadius("8px"),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  securityTitle: {
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  securityDescription: {
    marginTop: "2px",
    marginBottom: 0,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
  },
  signInButton: {
    width: "100%",
    height: "40px",
    justifyContent: "space-between",
    color: tokens.colorNeutralForegroundOnBrand,
    backgroundColor: tokens.colorBrandBackground,
    ...shorthands.border("1px", "solid", tokens.colorBrandBackground),
    ...shorthands.borderRadius("8px"),
    boxShadow: "none",
    ":hover": {
      color: tokens.colorNeutralForegroundOnBrand,
      backgroundColor: tokens.colorBrandBackgroundHover,
      ...shorthands.borderColor(tokens.colorBrandBackgroundHover),
    },
  },
  error: {
    ...shorthands.border("1px", "solid", "#fecdd3"),
    ...shorthands.borderRadius("10px"),
    backgroundColor: "#fff1f2",
  },
  help: {
    margin: 0,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    textAlign: "center",
  },
});

/**
 * Presents the Microsoft Entra sign-in entry point for the Sourcestone task pane.
 */
export const LoginPage: React.FC = () => {
  const styles = useStyles();
  const { instance } = useMsal();
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const handleLogin = async (): Promise<void> => {
    setErrorMessage(null);
    setIsLoading(true);

    if (typeof console !== "undefined" && typeof console.info === "function") {
      console.info("[config] API_BASE_URL resolved to:", API_BASE_URL);
      console.info("[config] LOCAL_DEV_MODE active:", LOCAL_DV_MODE);
    }

    try {
      const authResult = await startPopupLogin(instance);
      await checkRegistrationStatus(authResult.accessToken);
    } catch (error: unknown) {
      console.error("Login/Registration failed:", error);
      if (error instanceof Error && error.name === "RegistrationForbiddenError") {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("We couldn't sign you in. Please try again or contact your administrator.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className={styles.root}>
      <Card className={styles.card}>
        <div className={styles.header}>
          <div className={styles.logoFrame}>
            <img className={styles.logo} src={logoFilled} alt="Sourcestone" />
          </div>

          <Badge
            appearance="outline"
            className={styles.badge}
            icon={<LockClosed24Regular />}
            shape="rounded"
          >
            Secure access
          </Badge>

          <h1 className={styles.title}>Welcome to Sourcestone</h1>
          <p className={styles.description}>
            Continue with your Microsoft 365 identity to access your workspace.
          </p>
        </div>

        <div className={styles.content}>
          <div className={styles.securityPanel}>
            <div className={styles.securityIcon} aria-hidden>
              <ShieldCheckmark24Regular />
            </div>
            <div>
              <p className={styles.securityTitle}>Microsoft Entra ID</p>
              <p className={styles.securityDescription}>
                Sign in securely with your organisational account
              </p>
            </div>
          </div>

          <Button
            appearance="primary"
            className={styles.signInButton}
            disabled={isLoading}
            icon={isLoading ? <Spinner size="tiny" /> : <ArrowRight24Regular />}
            iconPosition="after"
            onClick={handleLogin}
            type="button"
          >
            {isLoading ? "Signing in..." : "Sign in with Microsoft"}
          </Button>

          <div aria-live="polite">
            {errorMessage && (
              <MessageBar className={styles.error} intent="error">
                <MessageBarBody>{errorMessage}</MessageBarBody>
              </MessageBar>
            )}
          </div>

          <p className={styles.help}>Need help? Contact your Sourcestone administrator.</p>
        </div>
      </Card>
    </main>
  );
};

export default LoginPage;
